import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  AudioFile as AudioFileIcon,
  Book as BookIcon,
  Archive as ArchiveIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  FolderZip as FolderZipIcon,
} from '@mui/icons-material';
import { ref, uploadBytesResumable } from 'firebase/storage';
import { storage } from '../firebase';
import JSZip from 'jszip';
import { useAuth } from '../contexts/AuthContext';

interface FileUploadState {
  file: File;
  progress: number;
  status: 'uploading' | 'completed' | 'error' | 'processing';
  storagePath?: string;
  error?: string;
  extractedFiles?: ExtractedFile[];
}

interface ExtractedFile {
  name: string;
  size: number;
  type: string;
  isAudio: boolean;
  uploadStatus?: 'pending' | 'uploading' | 'completed' | 'error';
  progress?: number;
  storagePath?: string;
  zipEntryName?: string;
  error?: string;
}

const FileUpload: React.FC = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('upload', 'editar');
  const [uploads, setUploads] = useState<FileUploadState[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [processingZip, setProcessingZip] = useState(false);
  const [zipPreview, setZipPreview] = useState<ExtractedFile[]>([]);
  const [showZipDialog, setShowZipDialog] = useState(false);
  const [currentZipFile, setCurrentZipFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const allowedExtensions = ['.m4b', '.epub', '.zip'];
  const contentTypeByExtension: Record<string, string> = {
    '.m4b': 'audio/mp4',
    '.mp3': 'audio/mpeg',
    '.m4a': 'audio/mp4',
    '.wav': 'audio/wav',
    '.flac': 'audio/flac',
    '.ogg': 'audio/ogg',
    '.epub': 'application/epub+zip',
    '.zip': 'application/zip',
  };

  const sanitizeStorageSegment = (name: string, fallback = 'arquivo') => {
    const leafName = name.split(/[\\/]/).pop() || '';
    const sanitized = leafName
      // eslint-disable-next-line no-control-regex
      .replace(/[\u0000-\u001f\u007f]/g, '')
      .replace(/[^a-zA-Z0-9._-]/g, '_')
      .replace(/^\.+|\.+$/g, '');
    if (!sanitized || sanitized === '.' || sanitized === '..') return fallback;
    const dotIndex = sanitized.lastIndexOf('.');
    return dotIndex > 0
      ? `${sanitized.substring(0, dotIndex)}${sanitized.substring(dotIndex).toLowerCase()}`
      : sanitized;
  };

  const canonicalContentType = (fileName: string) =>
    contentTypeByExtension[fileName.toLowerCase().substring(fileName.lastIndexOf('.'))];

  const isValidFile = (file: File): boolean => {
    const extension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    return allowedExtensions.includes(extension);
  };

  const getFileIcon = (fileName: string) => {
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    switch (extension) {
      case '.m4b':
        return <AudioFileIcon color="primary" />;
      case '.epub':
        return <BookIcon color="primary" />;
      case '.zip':
        return <ArchiveIcon color="primary" />;
      default:
        return <CloudUploadIcon color="primary" />;
    }
  };

  const getFileTypeLabel = (fileName: string) => {
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    switch (extension) {
      case '.m4b':
        return 'Audiobook';
      case '.epub':
        return 'E-book';
      case '.zip':
        return 'Archive';
      default:
        return 'Unknown';
    }
  };

  const processZipFile = async (file: File): Promise<ExtractedFile[]> => {
    const zip = new JSZip();
    const zipContent = await zip.loadAsync(file);
    const extractedFiles: ExtractedFile[] = [];

    for (const [filename, zipEntry] of Object.entries(zipContent.files)) {
      if (!(zipEntry as any).dir) {
        const fileExtension = filename.toLowerCase().substring(filename.lastIndexOf('.'));
        const isAudio = ['.mp3', '.m4a', '.m4b', '.wav', '.flac', '.ogg'].includes(fileExtension);

        // Get file size using async method
        const blob = await (zipEntry as any).async('blob');

        extractedFiles.push({
          name: sanitizeStorageSegment(filename),
          size: blob.size,
          type: fileExtension,
          isAudio,
          uploadStatus: 'pending',
          zipEntryName: filename,
        });
      }
    }

    return extractedFiles;
  };

  const handleZipFile = async (file: File) => {
    setProcessingZip(true);
    try {
      const extractedFiles = await processZipFile(file);
      setZipPreview(extractedFiles);
      setCurrentZipFile(file);
      setShowZipDialog(true);
    } catch (error) {
      console.error('Error processing ZIP file:', error);
      // Fallback to normal upload if ZIP processing fails
      uploadFile(file);
    } finally {
      setProcessingZip(false);
    }
  };

  const uploadExtractedFiles = async (selectedFiles: ExtractedFile[]) => {
    if (!currentZipFile) return;

    console.log('=== Iniciando upload de arquivos extraídos ===');
    console.log(
      'Arquivos selecionados:',
      selectedFiles.map((f) => f.name)
    );
    console.log('ZIP file:', currentZipFile.name);

    setShowZipDialog(false);

    // Create a new upload entry for the ZIP file
    const zipUpload: FileUploadState = {
      file: currentZipFile,
      progress: 0,
      status: 'processing',
      extractedFiles: selectedFiles,
    };

    setUploads((prev) => [...prev, zipUpload]);

    // Get ZIP name for folder creation
    const zipName = sanitizeStorageSegment(
      currentZipFile.name.replace(/\.zip$/i, ''),
      'audiobook',
    );
    const folderPath = `titles/audiobooks/${zipName}`;

    console.log('ZIP name:', zipName);
    console.log('Folder path:', folderPath);

    // Upload each selected file
    for (let i = 0; i < selectedFiles.length; i++) {
      const extractedFile = selectedFiles[i];

      try {
        // Extract the file from ZIP
        const zip = new JSZip();
        const zipContent = await zip.loadAsync(currentZipFile);
        const zipEntry = zipContent.files[extractedFile.zipEntryName ?? extractedFile.name];

        if (!zipEntry) continue;

        // Convert to Blob
        const blob = await zipEntry.async('blob');
        const file = new File([blob], sanitizeStorageSegment(extractedFile.name), { type: 'audio/mpeg' });

        // Update status to uploading
        setUploads((prev) =>
          prev.map((upload) =>
            upload.file === currentZipFile
              ? {
                  ...upload,
                  extractedFiles: upload.extractedFiles?.map((ef) => (ef.name === extractedFile.name ? { ...ef, uploadStatus: 'uploading', progress: 0 } : ef)),
                }
              : upload
          )
        );

        console.log(`Uploading file: ${extractedFile.name} to ${folderPath}`);
        // Upload the file to the ZIP folder
        await uploadSingleFile(file, currentZipFile, extractedFile.name, folderPath);
      } catch (error) {
        console.error('Error uploading extracted file:', error);
        setUploads((prev) =>
          prev.map((upload) =>
            upload.file === currentZipFile
              ? {
                  ...upload,
                  extractedFiles: upload.extractedFiles?.map((ef) =>
                    ef.name === extractedFile.name ? { ...ef, uploadStatus: 'error', error: error instanceof Error ? error.message : 'Unknown error' } : ef
                  ),
                }
              : upload
          )
        );
      }
    }

    // Mark ZIP as completed
    setUploads((prev) => prev.map((upload) => (upload.file === currentZipFile ? { ...upload, status: 'completed' } : upload)));
  };

  const uploadSingleFile = async (file: File, zipFile?: File, extractedFileName?: string, customFolder?: string) => {
    try {
      // Create a unique filename with timestamp
      const timestamp = Date.now();
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

      // Determine the appropriate folder based on file type
      let folder = 'uploads';
      if (['.m4b', '.mp3', '.m4a', '.wav', '.flac', '.ogg'].includes(fileExtension)) {
        folder = 'titles/audiobooks';
      } else if (['.epub', '.pdf', '.mobi'].includes(fileExtension)) {
        folder = 'titles/ebooks';
      }

      // Use custom folder if provided (for ZIP extractions)
      if (customFolder) {
        folder = customFolder;
      }

      const safeFileName = sanitizeStorageSegment(extractedFileName ?? file.name);
      const fileName = `${folder}/${timestamp}_${safeFileName}`;
      const contentType = canonicalContentType(safeFileName);
      if (!contentType) throw new Error('Tipo de arquivo não suportado.');

      console.log('Final file path:', fileName);
      console.log('File type:', file.type);
      console.log('File size:', file.size);

      const storageRef = ref(storage, fileName);

      // Start upload
      const uploadTask = uploadBytesResumable(storageRef, file, { contentType });

      return new Promise<void>((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;

            if (zipFile && extractedFileName) {
              setUploads((prev) =>
                prev.map((upload) =>
                  upload.file === zipFile
                    ? {
                        ...upload,
                        extractedFiles: upload.extractedFiles?.map((ef) => (ef.name === extractedFileName ? { ...ef, progress } : ef)),
                      }
                    : upload
                )
              );
            } else {
              setUploads((prev) => prev.map((upload) => (upload.file === file ? { ...upload, progress } : upload)));
            }
          },
          (error) => {
            console.error('Upload error:', error);
            reject(error);
          },
          () => {
            if (zipFile && extractedFileName) {
              setUploads((prev) =>
                prev.map((upload) =>
                  upload.file === zipFile
                    ? {
                        ...upload,
                        extractedFiles: upload.extractedFiles?.map((ef) =>
                          ef.name === extractedFileName ? { ...ef, uploadStatus: 'completed', storagePath: fileName } : ef
                        ),
                      }
                    : upload
                )
              );
            } else {
              setUploads((prev) => prev.map((upload) => (upload.file === file ? { ...upload, status: 'completed', storagePath: fileName } : upload)));
            }

            resolve();
          }
        );
      });
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  };

  const uploadFile = async (file: File) => {
    // Check if it's a ZIP file
    const extension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (extension === '.zip') {
      await handleZipFile(file);
      return;
    }

    const fileUpload: FileUploadState = {
      file,
      progress: 0,
      status: 'uploading',
    };

    setUploads((prev) => [...prev, fileUpload]);

    try {
      await uploadSingleFile(file);
    } catch (error) {
      console.error('Upload error:', error);
      setUploads((prev) =>
        prev.map((upload) => (upload.file === file ? { ...upload, status: 'error', error: error instanceof Error ? error.message : 'Unknown error' } : upload))
      );
    }
  };

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;

    Array.from(files).forEach((file) => {
      if (isValidFile(file)) {
        uploadFile(file);
      }
    });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (!canManage) return;
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const removeUpload = (file: File) => {
    setUploads((prev) => prev.filter((upload) => upload.file !== file));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const renderExtractedFiles = (upload: FileUploadState) => {
    if (!upload.extractedFiles) return null;

    return (
      <Box sx={{ mt: 2, ml: 4 }}>
        <Typography variant="subtitle2" gutterBottom>
          Arquivos Extraídos:
        </Typography>
        <List dense>
          {upload.extractedFiles.map((extractedFile, index) => (
            <ListItem key={index}>
              <ListItemIcon>{extractedFile.isAudio ? <AudioFileIcon color="primary" /> : <FolderZipIcon />}</ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {extractedFile.name}
                    </Typography>
                    <Chip
                      label={extractedFile.uploadStatus || 'pending'}
                      size="small"
                      color={
                        extractedFile.uploadStatus === 'completed'
                          ? 'success'
                          : extractedFile.uploadStatus === 'error'
                          ? 'error'
                          : extractedFile.uploadStatus === 'uploading'
                          ? 'info'
                          : 'default'
                      }
                    />
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(extractedFile.size)}
                    </Typography>
                    {extractedFile.uploadStatus === 'uploading' && extractedFile.progress !== undefined && (
                      <Box sx={{ mt: 1 }}>
                        <LinearProgress variant="determinate" value={extractedFile.progress} sx={{ mb: 0.5 }} />
                        <Typography variant="caption">{Math.round(extractedFile.progress)}%</Typography>
                      </Box>
                    )}
                    {extractedFile.uploadStatus === 'completed' && (
                      <IconButton size="small" sx={{ mt: 0.5 }} disabled>
                        <CheckCircleIcon color="success" fontSize="small" />
                      </IconButton>
                    )}
                    {extractedFile.uploadStatus === 'error' && (
                      <Typography variant="caption" color="error">
                        {extractedFile.error}
                      </Typography>
                    )}
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      </Box>
    );
  };

  return (
    <>
      <Paper sx={{ p: 3, m: 2 }}>
        <Typography variant="h6" gutterBottom>
          Upload de Arquivos
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Formatos suportados: M4B (audiobooks), EPUB (e-books), ZIP (arquivos compactados com MP3s)
        </Typography>

        {/* Upload Area */}
        <Tooltip title={canManage ? '' : 'Você não tem permissão de edição no módulo Upload.'}>
          <Box
            sx={{
              border: '2px dashed',
              borderColor: isDragging ? 'primary.main' : 'grey.300',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              backgroundColor: isDragging ? 'action.hover' : 'background.paper',
              transition: 'all 0.2s ease',
              cursor: 'pointer',
              mb: 3,
            }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => {
              if (!canManage) return;
              fileInputRef.current?.click();
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Arraste arquivos aqui ou clique para selecionar
            </Typography>
            <Typography variant="body2" color="text.secondary">
              M4B, EPUB, ZIP
            </Typography>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".m4b,.epub,.zip,audio/mp4,application/epub+zip,application/zip,application/x-zip-compressed"
              style={{ display: 'none' }}
              onChange={(e) => handleFileSelect(e.target.files)}
            />
          </Box>
        </Tooltip>

        {/* Upload Progress */}
        {uploads.length > 0 && (
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Arquivos ({uploads.length})
            </Typography>

            <List>
              {uploads.map((upload, index) => (
                <React.Fragment key={`${upload.file.name}-${index}`}>
                  <ListItem>
                    <ListItemIcon>{getFileIcon(upload.file.name)}</ListItemIcon>

                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" noWrap sx={{ flex: 1 }}>
                            {upload.file.name}
                          </Typography>
                          <Chip label={getFileTypeLabel(upload.file.name)} size="small" variant="outlined" />
                          <Typography variant="caption" color="text.secondary">
                            {formatFileSize(upload.file.size)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          {upload.status === 'uploading' && (
                            <Box>
                              <LinearProgress variant="determinate" value={upload.progress} sx={{ mb: 1 }} />
                              <Typography variant="caption">{Math.round(upload.progress)}% - Enviando...</Typography>
                            </Box>
                          )}

                          {upload.status === 'processing' && (
                            <Box>
                              <LinearProgress variant="indeterminate" sx={{ mb: 1 }} />
                              <Typography variant="caption">Processando arquivo ZIP...</Typography>
                            </Box>
                          )}

                          {upload.status === 'completed' && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CheckCircleIcon color="success" fontSize="small" />
                              <Typography variant="caption" color="success.main">
                                Upload concluído
                              </Typography>
                            </Box>
                          )}

                          {upload.status === 'error' && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <ErrorIcon color="error" fontSize="small" />
                              <Typography variant="caption" color="error.main">
                                Erro: {upload.error}
                              </Typography>
                            </Box>
                          )}

                          {renderExtractedFiles(upload)}
                        </Box>
                      }
                    />

                    <IconButton onClick={() => removeUpload(upload.file)} color="error" size="small">
                      <DeleteIcon />
                    </IconButton>
                  </ListItem>

                  {index < uploads.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Box>
        )}
      </Paper>

      {/* ZIP Preview Dialog */}
      <Dialog open={showZipDialog} onClose={() => setShowZipDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Arquivos Encontrados no ZIP</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Os seguintes arquivos serão extraídos e enviados automaticamente:
          </Typography>

          <List>
            {zipPreview.map((file, index) => (
              <ListItem key={index}>
                <ListItemIcon>{file.isAudio ? <AudioFileIcon color="primary" /> : <FolderZipIcon />}</ListItemIcon>
                <ListItemText primary={file.name} secondary={`${formatFileSize(file.size)} - ${file.type}`} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowZipDialog(false)}>Cancelar</Button>
          <Button onClick={() => uploadExtractedFiles(zipPreview.filter((f) => f.isAudio))} variant="contained" disabled={processingZip}>
            Confirmar e Fazer Upload
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FileUpload;
